<template>
  <ChartCard :data-widget-id="widgetId" :id-for-aria="widgetId">
    <ChartHeader
      :label="widgetLabel"
      :widget-download-id="widgetId"
      tooltip-key="gender-male-vs-female"
    />
    <ChartContent
      height="292px"
      :empty-data="emptyData || noWidgetData"
      :error="error"
      :loading="loading"
      type="horizontalBarChart"
      class="pl-4 pr-3 pt-6 pb-0"
    >
      <BarChart :id="widgetId" :data="chartData" :options="chartOptions" :plugins="plugins" title="Difference between male and female passthrough rates" />
      <div class="bottom-labels">
        <span>Male Advantage</span>
        <span>Female Advantage</span>
      </div>
    </ChartContent>
  </ChartCard>
</template>

<script setup>
import ChartContent from '@/components/insights/Chart/ChartContent.vue';
import { computed } from 'vue';
import { useColors } from '@/composables/colors';
import { customTooltip } from '@/components/insights/Chart/oldChartTooltip';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { formatTooltipValue } from '@/helpers/formatters';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import BarChart from '@/components/insights/Chart/BarChart.vue';
import { basicYScaleConfig } from '@/api/helpers/insights/commonChartConfigs';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  emptyData: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const widgetLabel = 'What is the difference between male and female pass-through rates?';

const plugins = [ChartDataLabels];

const widgetId = 'female-vs-male-passthrough-rate';

const {
  textDarkest, basicRed, grey400, white, brandPrimaryDark,
} = useColors();

const getPassthroughRate = (currentStageKey, nextStageKey, genderData) => {
  const rate = genderData[currentStageKey] > 0
    ? (genderData[nextStageKey] / genderData[currentStageKey]) * 100
    : 0;

  return rate;
};

const usableData = computed(() => {
  const { female, male } = props.data || {};

  const maleReviewToScreenPassthrough = getPassthroughRate('review', 'screen', male);
  const maleScreenToAssessmentPassthrough = getPassthroughRate('screen', 'assessment', male);
  const maleAssessmentToOfferPassthrough = getPassthroughRate('assessment', 'offer', male);
  const maleOfferToHiredPassthrough = getPassthroughRate('offer', 'hired', male);

  const femaleReviewToScreenPassthrough = getPassthroughRate('review', 'screen', female);
  const femaleScreenToAssessmentPassthrough = getPassthroughRate('screen', 'assessment', female);
  const femaleAssessmentToOfferPassthrough = getPassthroughRate('assessment', 'offer', female);
  const femaleOfferToHiredPassthrough = getPassthroughRate('offer', 'hired', female);

  return [
    femaleReviewToScreenPassthrough - maleReviewToScreenPassthrough,
    femaleScreenToAssessmentPassthrough - maleScreenToAssessmentPassthrough,
    femaleAssessmentToOfferPassthrough - maleAssessmentToOfferPassthrough,
    femaleOfferToHiredPassthrough - maleOfferToHiredPassthrough,
  ];
});

const noWidgetData = computed(() => !props.data
  || Object.keys(props.data).length === 0
  || Object.keys(props.data).every((key) => {
    const stageData = { ...props.data[key] };

    delete stageData?.review;

    return !stageData || Object.values(stageData).every((value) => !value);
  }));

const chartData = computed(() => ({
  labels: ['Screening', 'Assessment', 'Offer', 'Hired'],
  datasets: [
    {
      label: '',
      backgroundColor: usableData.value
        .map((val) => (Math.abs(val) > 5 ? basicRed.value : brandPrimaryDark.value)),
      borderColor: usableData.value
        .map((val) => (Math.abs(val) > 5 ? basicRed.value : brandPrimaryDark.value)),
      maxBarThickness: 24,
      barThickness: 'flex',
      data: usableData.value,
    },
  ],
}));

const absoluteValueForMinMax = computed(() => {
  const values = usableData.value || [];
  const minMaxValue = Math.ceil(Math.max(...values.map(Math.abs)));
  const lowestMinMaxValue = 10;

  return Math.max(minMaxValue, lowestMinMaxValue);
});

const absoluteValueOfDatasets = computed(() => (chartData.value?.datasets || []).map((dataset) => ({
  ...dataset,
  data: dataset.data.map((value) => Math.abs(value)),
})));

const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  responsive: true,
  indexAxis: 'y',
  plugins: {
    legend: { display: false },
    tooltip: {
      mode: 'index',
      enabled: false,
      external: customTooltip(
        'basicChart',
        {
          chartData: {
            originalLables: chartData.value.labels,
            datasets: absoluteValueOfDatasets.value,
          },
          isFlippedAxis: true,
          labelFormat: 'percent',
        },
        widgetId,
      ),
      bodyFont: {
        size: 16,
        family: 'Inter',
      },
    },
    datalabels: {
      color: function colorCallback(context) {
        const value = Math.abs(context.dataset.data[context.dataIndex]);
        const percentOfSpace = (value / absoluteValueForMinMax.value) * 100;

        return percentOfSpace < 25 ? textDarkest.value : white.value;
      },
      anchor: function anchorCallback(context) {
        const value = context.dataset.data[context.dataIndex];

        return value < 0 ? 'start' : 'end';
      },
      align: function alignCallback(context) {
        const value = context.dataset.data[context.dataIndex];
        const absValue = Math.abs(value);
        const percentOfSpace = Number.isNaN((absValue / absoluteValueForMinMax.value) * 100)
          ? 0 : (absValue / absoluteValueForMinMax.value) * 100;
        const isNegative = value < 0;

        return (percentOfSpace < 25 && !isNegative) || (isNegative && percentOfSpace >= 30) ? 'end' : 'start';
      },
      formatter: function formatterCallback(value) {
        if (Number.isNaN(value)) return '';
        return formatTooltipValue(Math.abs(value), 'percent');
      },
    },
  },
  scales: {
    x: {
      title: {
        display: false,
      },
      beginAtZero: true,
      border: {
        display: false,
        dash: [2, 3],
      },
      grid: {
        display: true,
        color: grey400.value,
        tickLength: 0,
      },
      ticks: {
        padding: 16,
        callback: function xTickCallback(value) {
          return `${Math.round(Math.abs(value * 10)) / 10}%`;
        },
      },
      min: -absoluteValueForMinMax.value,
      max: absoluteValueForMinMax.value,
    },
    y: basicYScaleConfig,
  },
}));
</script>

<style lang="less" scoped>
.bottom-labels {
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  width: calc(100% - 80px);
  margin: -10px 0 12px 80px;

  span {
    font-size: 12px;
    color: @text-light;
  }
}
</style>
